import { Team } from '@skop-app/lib-domain/types'

export type OrgTeamMembership = {
  id?: string
  value: string
  label: string
  role?: string
}

export type OrgTeamMember = {
  id: string
  userID: string
  name?: string
  email?: string
  role: string
  status: string
  projectID?: string | null
  organizationID: string
  organizationName?: string
  activeMembershipId?: string
  teams: OrgTeamMembership[]
  sendEmail?: boolean
}

const getMemberIdentityKey = (member: {
  email?: string | null
  userID?: string | null
  id?: string | null
}) => member.email ?? member.userID ?? member.id ?? ''

const deduplicateMembers = <
  T extends { email?: string; userID?: string; id?: string },
>(
  members: T[],
) =>
  members.filter((member, index, array) => {
    const identityKey = getMemberIdentityKey(member)
    return (
      identityKey.length > 0 &&
      index ===
        array.findIndex(
          (candidate) => getMemberIdentityKey(candidate) === identityKey,
        )
    )
  })

export const formatOrganizationData = (
  organization: Team | undefined,
  selectedTeam: string,
) => {
  // Format the data to be used in the table
  const allTeams = organization?.teams ?? []
  const allMembers = organization?.members ?? []

  const groupTeamsByMemberID = allMembers.reduce((acc, member) => {
    const id = getMemberIdentityKey(member)
    if (!id) return acc
    if (!acc[id]) {
      acc[id] = []
    }
    acc[id].push({
      value: member.organizationID,
      label: member.organizationName,
      id: member.id, // used to update the member
      role: member.role,
    })
    return acc
  }, {})

  const labelValueTeams = [
    // All teams members (including master org members)
    {
      label: 'Toutes les équipes',
      value: 'all',
      members: deduplicateMembers(allMembers),
      master: null,
      parent: null,
      color: '#CCC',
      logo: organization?.logo,
    },
    // Master org members only
    {
      label:
        allTeams?.[0]?.master?.name ?? organization?.name ?? '(Nom manquant)',
      value: allTeams?.[0]?.master?.id ?? organization?.id ?? 'unknown',
      color: allTeams?.[0]?.master?.color ?? organization?.color ?? '#CCC',
      logo: allTeams?.[0]?.master?.logo ?? organization?.logo,
      members: organization?.members ?? [],
      master: allTeams?.[0]?.master ?? organization,
      parent: null,
    },
    // Subteams members
    ...allTeams.map((team) => ({
      label: team.name,
      value: team.id,
      color: team.color,
      logo: team.logo,
      members: team.members,
      master: team.master,
      parent: team.parent,
    })),
  ]

  const currentTeam = labelValueTeams.find(
    (team) => team.value === selectedTeam,
  )

  const data =
    currentTeam?.members
      .filter(
        (member) =>
          currentTeam?.value === 'all' ||
          member.organizationID === currentTeam?.value,
      )
      .map((member) => {
        const id = getMemberIdentityKey(member)
        return {
          ...member,
          activeMembershipId:
            currentTeam?.value === 'all' ? undefined : member.id,
          teams: groupTeamsByMemberID[id] ?? [],
        }
      }) ?? []

  return {
    currentTeam,
    data: currentTeam?.value === 'all' ? deduplicateMembers(data) : data,
    selectOptions: labelValueTeams,
  }
}
